import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AuthHeader } from "@/components/AuthHeader";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex flex-col bg-background px-4">
        <div className="flex-1 flex items-center justify-start sm:justify-center pt-16 sm:pt-0 pb-8">
          <div className="w-full max-w-sm text-center animate-fade-in mx-auto">
            <Link to="/" className="flex items-center justify-center gap-2 mb-8">
              <Logo size={32} />
              <span className="font-semibold text-xl text-foreground">cenik.ai</span>
            </Link>
            <h1 className="text-2xl font-semibold text-foreground mb-4">{t("forgotPassword.sent")}</h1>
            <p className="text-muted-foreground text-sm mb-6">{t("forgotPassword.sentDesc")}</p>
            <Link
              to="/login"
              className="inline-block px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:opacity-90 transition-opacity"
            >
              {t("forgotPassword.backToLogin")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AuthHeader backTo="/login" infoKey="forgotPassword.info" />
      <div className="flex-1 flex items-center justify-start sm:justify-center px-4 pt-6 sm:pt-0 pb-8 overflow-y-auto">
      <div className="w-full max-w-sm animate-fade-in">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6 sm:mb-8">
            <Logo size={32} />
            <span className="font-semibold text-xl text-foreground">cenik.ai</span>
        </Link>

        <h1 className="text-2xl font-semibold text-foreground text-center mb-2">{t("forgotPassword.title")}</h1>
        <p className="text-sm text-muted-foreground text-center mb-6">{t("forgotPassword.description")}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t("login.email")}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder={t("login.emailPlaceholder")}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "..." : t("forgotPassword.submit")}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/login" className="text-foreground font-medium hover:underline">
            {t("forgotPassword.backToLogin")}
          </Link>
        </p>
      </div>
      </div>
    </div>
  );
}
